import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatRupees, formatRupeesCompact, formatDate, todayISO, INVESTMENT_TYPE_LABEL } from "@/lib/format";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, IndianRupee, CheckCircle2, CircleDashed, CircleAlert, X as XIcon, Search } from "lucide-react";
import FYPicker, { currentFYStart, fyToRange } from "@/components/FYPicker";
import MonthPicker, { monthToRange } from "@/components/MonthPicker";

const STATUS = {
  open: { icon: CircleDashed, class: "bg-yellow-50 text-yellow-700 border-yellow-200", label: "OPEN" },
  partial: { icon: CircleAlert, class: "bg-orange-50 text-orange-700 border-orange-200", label: "PARTIAL" },
  paid: { icon: CheckCircle2, class: "bg-green-50 text-green-700 border-green-200", label: "PAID" },
};

const emptyLine = () => ({ description: "", amount: "", category_id: "", sub_category_id: "" });

const emptyForm = () => ({
  number: "", date: todayISO(), due_date: "",
  party_id: "", party_name: "", party_phone: "", party_email: "", party_gstin: "",
  description: "", subtotal: "", gst_percent: 18, gst_amount: "", total: "",
  category_id: "", sub_category_id: "", investment_id: "", notes: "",
  lines: [],
});

/**
 * Shared component: renders as Bills (kind=bill) or Invoices (kind=invoice).
 */
export default function BillsPage({ kind = "bill" }) {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "accounts";
  const isBill = kind === "bill";
  const titleWord = isBill ? "bills" : "invoices";
  const partyLabel = isBill ? "Vendor" : "Customer";

  const [bills, setBills] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subs, setSubs] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [parties, setParties] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [fy, setFy] = useState(currentFYStart());
  const [month, setMonth] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [payDlg, setPayDlg] = useState(null);   // bill to receive payment against
  const [payForm, setPayForm] = useState({ date: todayISO(), amount: "", account_id: "", reference: "", note: "" });

  const load = async () => {
    const params = new URLSearchParams({ kind });
    if (statusFilter) params.set("status", statusFilter);
    if (projectFilter) params.set("investment_id", projectFilter);
    if (month) {
      const { from, to } = monthToRange(month);
      if (from) params.set("from_date", from);
      if (to) params.set("to_date", to);
    } else {
      const { from, to } = fyToRange(fy);
      if (from) params.set("from_date", from);
      if (to) params.set("to_date", to);
    }
    const partyKind = isBill ? "vendor" : "customer";
    const [b, a, c, s, i, p] = await Promise.all([
      api.get(`/bills?${params.toString()}`),
      api.get("/accounts"),
      api.get("/categories"),
      api.get("/subcategories"),
      api.get("/investments"),
      api.get(`/parties?kind=${partyKind}`),
    ]);
    setBills(b.data); setAccounts(a.data); setCategories(c.data); setSubs(s.data); setInvestments(i.data); setParties(p.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, statusFilter, fy, month, projectFilter]);

  // Refetch masters when the entry-form project changes — only masters applicable to that project.
  useEffect(() => {
    if (!form.investment_id) return; // keep the full lists from load()
    const partyKind = isBill ? "vendor" : "customer";
    const params = { investment_id: form.investment_id };
    Promise.all([
      api.get("/accounts", { params }),
      api.get("/categories", { params }),
      api.get("/subcategories", { params }),
      api.get(`/parties`, { params: { ...params, kind: partyKind } }),
    ]).then(([a, c, s, p]) => {
      setAccounts(a.data); setCategories(c.data); setSubs(s.data); setParties(p.data);
    }).catch(() => {});
  }, [form.investment_id, isBill]);

  const visibleBills = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return bills;
    return bills.filter((b) =>
      (b.party_name || "").toLowerCase().includes(s) ||
      (b.number || "").toLowerCase().includes(s) ||
      (b.description || "").toLowerCase().includes(s) ||
      (b.notes || "").toLowerCase().includes(s)
    );
  }, [bills, q]);

  const catsFiltered = useMemo(
    () => categories.filter((c) => (isBill ? c.kind !== "income" : c.kind !== "expense")),
    [categories, isBill]
  );
  const subsForCat = useMemo(
    () => subs.filter((s) => s.category_id === form.category_id),
    [subs, form.category_id]
  );

  const recompute = (f) => {
    // If lines exist, subtotal = sum of line amounts (lines drive the total)
    const hasLines = (f.lines || []).length > 0;
    const linesSum = (f.lines || []).reduce((a, l) => a + Number(l.amount || 0), 0);
    const sub = hasLines ? linesSum : Number(f.subtotal || 0);
    const gst = sub * Number(f.gst_percent || 0) / 100;
    const total = sub + gst;
    return {
      ...f,
      subtotal: hasLines ? sub.toFixed(2) : f.subtotal,
      gst_amount: gst.toFixed(2),
      total: total.toFixed(2),
    };
  };

  const addLine = () => setForm((f) => recompute({ ...f, lines: [...(f.lines || []), emptyLine()] }));
  const removeLine = (idx) => setForm((f) => recompute({ ...f, lines: (f.lines || []).filter((_, i) => i !== idx) }));
  const patchLine = (idx, patch) => setForm((f) => {
    const lines = (f.lines || []).map((l, i) => (i === idx ? { ...l, ...patch } : l));
    return recompute({ ...f, lines });
  });

  const openAdd = () => { setEditing(null); setForm(emptyForm()); setDlg(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({ ...b,
      subtotal: b.subtotal || "", gst_amount: b.gst_amount || "", total: b.total || "",
      category_id: b.category_id || "", sub_category_id: b.sub_category_id || "",
      investment_id: b.investment_id || "", due_date: b.due_date || "",
      lines: (b.lines || []).map((l) => ({
        description: l.description || "",
        amount: l.amount ?? "",
        category_id: l.category_id || "",
        sub_category_id: l.sub_category_id || "",
      })),
    });
    setDlg(true);
  };

  // Handle party name select from datalist — autofill GSTIN, phone, email
  const onPartyNameChange = (name) => {
    const match = parties.find((p) => p.name.toLowerCase() === name.toLowerCase());
    if (match) {
      setForm((f) => ({
        ...f,
        party_id: match.id,
        party_name: match.name,
        party_gstin: f.party_gstin || match.gstin || "",
        party_phone: f.party_phone || match.phone || "",
        party_email: f.party_email || match.email || "",
      }));
    } else {
      setForm((f) => ({ ...f, party_id: "", party_name: name }));
    }
  };

  const save = async () => {
    if (!form.number || !form.party_name) return toast.error("Number and party required");
    const lines = (form.lines || [])
      .filter((l) => l.description || Number(l.amount || 0) > 0)
      .map((l) => ({
        description: l.description || "",
        amount: Number(l.amount || 0),
        category_id: l.category_id || null,
        sub_category_id: l.sub_category_id || null,
      }));
    if (lines.length === 0 && !form.subtotal) return toast.error("Add at least one line or a subtotal");
    // If lines exist, ensure vendor set a party name — done above
    const payload = {
      ...form, kind,
      subtotal: Number(form.subtotal || 0),
      gst_percent: Number(form.gst_percent || 0),
      gst_amount: Number(form.gst_amount || 0),
      total: Number(form.total || 0),
      category_id: form.category_id || null,
      sub_category_id: form.sub_category_id || null,
      investment_id: form.investment_id || null,
      lines,
    };
    // Remove non-backend fields
    delete payload.party_id;
    try {
      if (editing) await api.put(`/bills/${editing.id}`, payload);
      else await api.post("/bills", payload);
      // Auto-create a party if the typed name didn't match any existing party
      const existsInParties = parties.some((p) => p.name.toLowerCase() === form.party_name.toLowerCase());
      if (!existsInParties && form.party_name) {
        try {
          await api.post("/parties", {
            kind: isBill ? "vendor" : "customer",
            name: form.party_name,
            phone: form.party_phone || "",
            email: form.party_email || "",
            gstin: form.party_gstin || "",
          });
        } catch (_) { /* non-fatal */ }
      }
      toast.success(editing ? "Updated" : "Saved");
      setDlg(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm(`Delete this ${isBill ? "bill" : "invoice"}?`)) return;
    try {
      await api.delete(`/bills/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openPay = (b) => {
    setPayDlg(b);
    setPayForm({ date: todayISO(), amount: b.outstanding || b.total, account_id: accounts[0]?.id || "", reference: b.number || "", note: "" });
  };

  const recordPayment = async () => {
    if (!payForm.amount || !payForm.account_id) return toast.error("Amount and account required");
    try {
      await api.post(`/bills/${payDlg.id}/payments`, { ...payForm, amount: Number(payForm.amount) });
      toast.success(isBill ? "Payment recorded" : "Receipt recorded");
      setPayDlg(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const totalOpen = bills.filter((b) => b.status !== "paid").reduce((a, b) => a + (b.outstanding || 0), 0);
  const totalAll = bills.reduce((a, b) => a + (b.total || 0), 0);
  const paidAll = bills.reduce((a, b) => a + (b.paid_amount || 0), 0);

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">{isBill ? "Payables" : "Receivables"}</div>
          <h1 className="mt-2 font-editorial font-light text-[38px] leading-[1.02] sm:text-5xl md:text-6xl text-slate-900 tracking-[-0.035em]">
            {isBill ? <>Purchase <em className="italic">bills.</em></> : <><em className="italic">Sales</em> invoices.</>}
          </h1>
          <p className="mt-2 text-sm text-slate-500 max-w-md">
            {isBill
              ? "Bills received from vendors. Record payments against each — every payment is captured in the Cash Book."
              : "Invoices raised on customers. Record receipts as they come in — every receipt flows to the Cash Book."}
          </p>
        </div>
        {canEdit && (
          <Link to={isBill ? "/bills/new" : "/invoices/new"}>
            <Button className="rounded-md bg-red-600 hover:bg-red-700 text-white gap-2" data-testid="add-bill-btn">
              <Plus className="h-4 w-4" /> Add {isBill ? "bill" : "invoice"}
            </Button>
          </Link>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="border border-slate-200 bg-white rounded-md p-4">
          <div className="eyebrow">Total {titleWord}</div>
          <div className="mt-1 font-mono-fin text-xl text-slate-900">{formatRupeesCompact(totalAll)}</div>
        </div>
        <div className="border border-slate-200 bg-white rounded-md p-4">
          <div className="eyebrow">{isBill ? "Paid" : "Received"}</div>
          <div className="mt-1 font-mono-fin text-xl text-green-700">{formatRupeesCompact(paidAll)}</div>
        </div>
        <div className="border border-slate-200 bg-white rounded-md p-4">
          <div className="eyebrow">Outstanding</div>
          <div className="mt-1 font-mono-fin text-xl text-red-600">{formatRupeesCompact(totalOpen)}</div>
        </div>
        <div className="border border-slate-200 bg-white rounded-md p-4">
          <div className="eyebrow">Count</div>
          <div className="mt-1 font-mono-fin text-xl text-slate-900">{bills.length}</div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        {["", "open", "partial", "paid"].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setStatusFilter(s)}
            className={`text-[12px] font-medium px-3 py-1.5 rounded-full border transition-colors ${
              statusFilter === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
            }`}
            data-testid={`bill-status-${s || "all"}`}
          >
            {s ? s.charAt(0).toUpperCase() + s.slice(1) : "All"}
          </button>
        ))}
        <div className="relative flex-1 min-w-[160px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" strokeWidth={1.75} />
          <Input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${partyLabel.toLowerCase()}, number, note…`}
            className="pl-9 h-9" data-testid="bill-search"
          />
        </div>
        <FYPicker value={fy} onChange={(v) => { setFy(v); setMonth(""); }} size="sm" />
        <MonthPicker value={month} onChange={setMonth} fy={fy} size="sm" />
        <Select value={projectFilter || "all"} onValueChange={(v) => setProjectFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[190px] h-9 text-[12px]" data-testid="bill-project-filter">
            <SelectValue placeholder="Project" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All projects</SelectItem>
            {investments.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      <div className="hidden md:block border border-slate-200 rounded-md overflow-x-auto bg-white">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {["No.", "Date", "Due", partyLabel, "Category", "Total", isBill ? "Paid" : "Received", "Outstanding", "Status", ""].map((h) => (
                <th key={h} className={`p-3 text-[10px] tracking-wider uppercase text-slate-500 ${["Total", "Outstanding", "Paid", "Received"].includes(h) ? "text-right" : "text-left"}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleBills.map((b) => {
              const S = STATUS[b.status] || STATUS.open;
              const cat = categories.find((c) => c.id === b.category_id);
              return (
                <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`bill-${b.id}`}>
                  <td className="p-3 font-medium text-slate-900">{b.number}</td>
                  <td className="p-3 text-slate-600 whitespace-nowrap">{formatDate(b.date)}</td>
                  <td className="p-3 text-slate-500 whitespace-nowrap">{b.due_date ? formatDate(b.due_date) : "—"}</td>
                  <td className="p-3 text-slate-900 truncate max-w-[180px]">{b.party_name}</td>
                  <td className="p-3 text-slate-600 text-[12px]">{cat?.name || "—"}</td>
                  <td className="p-3 text-right font-mono-fin">{formatRupees(b.total)}</td>
                  <td className="p-3 text-right font-mono-fin text-green-700">{formatRupees(b.paid_amount || 0)}</td>
                  <td className={`p-3 text-right font-mono-fin ${b.outstanding > 0 ? "text-red-600" : "text-slate-400"}`}>{formatRupees(b.outstanding || 0)}</td>
                  <td className="p-3">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-medium tracking-wider uppercase px-2 py-0.5 rounded border ${S.class}`}>
                      <S.icon className="h-3 w-3" strokeWidth={2} /> {S.label}
                    </span>
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {canEdit && (
                      <div className="flex items-center gap-1 justify-end">
                        {b.status !== "paid" && (
                          <button onClick={() => openPay(b)} className="text-[11px] font-medium text-red-600 hover:text-red-700" data-testid={`pay-${b.id}`}>
                            {isBill ? "Pay" : "Receive"}
                          </button>
                        )}
                        <button onClick={() => openEdit(b)} className="p-1 text-slate-400 hover:text-slate-700"><Pencil className="h-3.5 w-3.5" /></button>
                        <button onClick={() => remove(b.id)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {visibleBills.length === 0 && (
              <tr><td colSpan={10} className="p-10 text-center text-slate-500 text-sm">No {titleWord} match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden grid gap-2">
        {visibleBills.map((b) => {
          const S = STATUS[b.status] || STATUS.open;
          return (
            <div key={b.id} className="border border-slate-200 rounded-md bg-white p-3.5" data-testid={`bill-mobile-${b.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[11px] text-slate-500">{b.number} · {formatDate(b.date)}</div>
                  <div className="text-[13px] font-medium text-slate-900 mt-0.5 truncate">{b.party_name}</div>
                </div>
                <span className={`inline-flex items-center gap-1 text-[9px] font-medium tracking-wider uppercase px-1.5 py-0.5 rounded border ${S.class}`}>
                  <S.icon className="h-2.5 w-2.5" /> {S.label}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-slate-100 text-[11px]">
                <div><div className="text-slate-400">Total</div><div className="font-mono-fin text-slate-900 mt-0.5">{formatRupeesCompact(b.total)}</div></div>
                <div><div className="text-slate-400">{isBill ? "Paid" : "Recv"}</div><div className="font-mono-fin text-green-700 mt-0.5">{formatRupeesCompact(b.paid_amount || 0)}</div></div>
                <div><div className="text-slate-400">Due</div><div className={`font-mono-fin mt-0.5 ${b.outstanding > 0 ? "text-red-600" : "text-slate-400"}`}>{formatRupeesCompact(b.outstanding || 0)}</div></div>
              </div>
              {canEdit && b.status !== "paid" && (
                <button onClick={() => openPay(b)} className="mt-3 text-[12px] font-medium text-red-600 w-full text-left">
                  {isBill ? "Record payment →" : "Record receipt →"}
                </button>
              )}
            </div>
          );
        })}
        {visibleBills.length === 0 && (
          <div className="p-8 text-center text-slate-500 text-[13px] border border-slate-200 rounded-md bg-white">
            No {titleWord} match these filters.
          </div>
        )}
      </div>
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} {isBill ? "purchase bill" : "sales invoice"}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto -mx-1 px-1">
            <div className="grid grid-cols-3 gap-3">
              <div><Label>{isBill ? "Bill" : "Invoice"} no.</Label><Input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} className="mt-2" data-testid="b-number" /></div>
              <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-2" /></div>
              <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-2" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{partyLabel} name</Label>
                <Input
                  value={form.party_name}
                  onChange={(e) => onPartyNameChange(e.target.value)}
                  className="mt-2"
                  data-testid="b-party"
                  list={`party-suggestions-${kind}`}
                  autoComplete="off"
                  placeholder={`Type to search ${partyLabel.toLowerCase()}s…`}
                />
                <datalist id={`party-suggestions-${kind}`}>
                  {parties.map((p) => (
                    <option key={p.id} value={p.name}>{p.gstin || p.phone || ""}</option>
                  ))}
                </datalist>
                {parties.length > 0 && (
                  <div className="text-[10px] text-slate-400 mt-1">
                    {parties.length} saved · new names are auto-added to Masters
                  </div>
                )}
              </div>
              <div><Label>GSTIN</Label><Input value={form.party_gstin} onChange={(e) => setForm({ ...form, party_gstin: e.target.value })} className="mt-2" /></div>
              <div><Label>Phone</Label><Input value={form.party_phone} onChange={(e) => setForm({ ...form, party_phone: e.target.value })} className="mt-2" /></div>
              <div><Label>Email</Label><Input value={form.party_email} onChange={(e) => setForm({ ...form, party_email: e.target.value })} className="mt-2" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={form.category_id || "none"} onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? "" : v, sub_category_id: "" })}>
                  <SelectTrigger className="mt-2"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {catsFiltered.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sub-category</Label>
                <Select value={form.sub_category_id || "none"} onValueChange={(v) => setForm({ ...form, sub_category_id: v === "none" ? "" : v })} disabled={!form.category_id}>
                  <SelectTrigger className="mt-2"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {subsForCat.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tag to investment</Label>
                <Select value={form.investment_id || "none"} onValueChange={(v) => setForm({ ...form, investment_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-2"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {investments.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-2" /></div>

            {/* Line items editor */}
            <div className="border border-slate-200 rounded-md p-3 bg-slate-50/50">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <Label className="text-[12px]">Line items</Label>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {(form.lines || []).length === 0
                      ? "Add rows to split by category — or leave empty and use subtotal below."
                      : "Subtotal below is auto-summed from these lines."}
                  </div>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addLine} className="h-7 gap-1 rounded-md text-[11px]" data-testid="add-line">
                  <Plus className="h-3 w-3" /> Add line
                </Button>
              </div>
              {(form.lines || []).length > 0 && (
                <div className="space-y-2">
                  {(form.lines || []).map((line, idx) => {
                    const catsForLine = catsFiltered;
                    const subsForLine = subs.filter((s) => s.category_id === line.category_id);
                    return (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-start" data-testid={`line-${idx}`}>
                        <div className="col-span-4">
                          <Input
                            value={line.description}
                            onChange={(e) => patchLine(idx, { description: e.target.value })}
                            placeholder="Description"
                            className="h-8 text-[12px]"
                            data-testid={`line-${idx}-desc`}
                          />
                        </div>
                        <div className="col-span-3">
                          <Select
                            value={line.category_id || "none"}
                            onValueChange={(v) => patchLine(idx, { category_id: v === "none" ? "" : v, sub_category_id: "" })}
                          >
                            <SelectTrigger className="h-8 text-[12px]" data-testid={`line-${idx}-cat`}>
                              <SelectValue placeholder="Category" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">— None —</SelectItem>
                              {catsForLine.map((c) => (
                                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-3">
                          <Select
                            value={line.sub_category_id || "none"}
                            onValueChange={(v) => patchLine(idx, { sub_category_id: v === "none" ? "" : v })}
                            disabled={!line.category_id}
                          >
                            <SelectTrigger className="h-8 text-[12px]">
                              <SelectValue placeholder="Sub-cat" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">— None —</SelectItem>
                              {subsForLine.map((s) => (
                                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-1">
                          <Input
                            type="number"
                            value={line.amount}
                            onChange={(e) => patchLine(idx, { amount: e.target.value })}
                            placeholder="0"
                            className="h-8 text-[12px] font-mono-fin text-right"
                            data-testid={`line-${idx}-amt`}
                          />
                        </div>
                        <div className="col-span-1 flex justify-end">
                          <button
                            type="button"
                            onClick={() => removeLine(idx)}
                            className="h-8 w-8 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center"
                            data-testid={`line-${idx}-remove`}
                          >
                            <XIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="grid grid-cols-4 gap-3">
              <div><Label>Subtotal</Label><Input type="number" value={form.subtotal} onChange={(e) => setForm(recompute({ ...form, subtotal: e.target.value }))} className="mt-2" data-testid="b-subtotal" disabled={(form.lines || []).length > 0} /></div>
              <div><Label>GST %</Label><Input type="number" value={form.gst_percent} onChange={(e) => setForm(recompute({ ...form, gst_percent: e.target.value }))} className="mt-2" /></div>
              <div><Label>GST amount</Label><Input type="number" value={form.gst_amount} readOnly className="mt-2 bg-slate-50" /></div>
              <div><Label>Total</Label><Input type="number" value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value })} className="mt-2 font-medium" /></div>
            </div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-2" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)} className="rounded-md">Cancel</Button>
            <Button onClick={save} className="rounded-md bg-red-600 hover:bg-red-700 text-white" data-testid="b-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment dialog */}
      <Dialog open={!!payDlg} onOpenChange={(o) => !o && setPayDlg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isBill ? "Record payment" : "Record receipt"} — {payDlg?.number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-500">
              Party: <span className="text-slate-900">{payDlg?.party_name}</span> ·
              Outstanding: <span className="font-mono-fin text-slate-900">{formatRupees(payDlg?.outstanding || 0)}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount</Label><Input type="number" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className="mt-2" data-testid="p-amount" /></div>
              <div><Label>Date</Label><Input type="date" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} className="mt-2" /></div>
            </div>
            <div>
              <Label>{isBill ? "Paid from account" : "Received into account"}</Label>
              <Select value={payForm.account_id} onValueChange={(v) => setPayForm({ ...payForm, account_id: v })}>
                <SelectTrigger className="mt-2" data-testid="p-account"><SelectValue placeholder="Choose account" /></SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} {a.account_number && `· ${a.account_number}`}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Reference (UTR / cheque)</Label><Input value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} className="mt-2" /></div>
              <div><Label>Note</Label><Input value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} className="mt-2" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayDlg(null)} className="rounded-md">Cancel</Button>
            <Button onClick={recordPayment} className="rounded-md bg-red-600 hover:bg-red-700 text-white gap-2" data-testid="p-save">
              <IndianRupee className="h-4 w-4" /> {isBill ? "Pay" : "Receive"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
